import { memo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar, AreaChart, Area,
} from "recharts";
import { RiskGauge, sevColor, HeatmapCell } from "./ReportCharts";
import ReportSignoff from "./ReportSignoff";
import ReportIntegrity from "./ReportIntegrity";

/* ── themes ─────────────────────────────────────────────────── */
const THEMES = {
  modern: {
    fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
    text: "#111827",
    muted: "#4b5563",
    surface: "#f8fafc",
    soft: "#f3f4f6",
    primary: "#dc2626",
    accent: "#991b1b",
    coverBg: "linear-gradient(165deg, #fef2f2 0%, #ffffff 42%, #fee2e2 100%)",
    coverBorder: "#dc2626",
    coverFrame: "rgba(220, 38, 38, 0.08)",
  },
  classic: {
    fontFamily: "'Times New Roman', Georgia, serif",
    text: "#1f2937",
    muted: "#4b5563",
    surface: "#f9fafb",
    soft: "#f3f4f6",
    primary: "#1f2937",
    accent: "#374151",
    coverBg: "linear-gradient(180deg, #f3f4f6 0%, #ffffff 48%, #f8fafc 100%)",
    coverBorder: "#1f2937",
    coverFrame: "rgba(31, 41, 55, 0.08)",
  },
  minimal: {
    fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
    text: "#111827",
    muted: "#4b5563",
    surface: "#ffffff",
    soft: "#e5e7eb",
    primary: "#111827",
    accent: "#6b7280",
    coverBg: "#ffffff",
    coverBorder: "#111827",
    coverFrame: "rgba(17, 24, 39, 0.06)",
    // minimal layout flag — the renderers below use this to drop boxes,
    // badges and tints in favour of flat type, hairline rules and whitespace.
    minimal: true,
  },
};

/* ── renderers ──────────────────────────────────────────────── */
const stripTechPrefixes = [
  /^SQL Policy Validation:\s*/i, /^SQL Validation:\s*/i,
  /^Policy Validation:\s*/i, /^Policy Check:\s*/i,
  /^Control Check:\s*/i, /^RAG Validation:\s*/i,
  /^RAG Check:\s*/i, /^Control Validation:\s*/i,
  /^Audit Control:\s*/i, /^Rule Check:\s*/i,
];

function stripTech(t) {
  let s = String(t || "");
  for (const re of stripTechPrefixes) s = s.replace(re, "");
  return s.replace(/_/g, " ").replace(/^[:\-–\s]+/, "").trim() || t;
}

const sevBorderColor = (s) => ({ Critical: "#991b1b", High: "#ef4444", Medium: "#f59e0b", Low: "#64748b", Observation: "#94a3b8" })[s] || "#cbd5e1";
const sevBgColor = (s) => ({ Critical: "#fef2f2", High: "#fef2f2", Medium: "#fffbeb", Low: "#f8fafc", Observation: "#f8fafc" })[s] || "#f9fafb";
const sevLineColor = (s) => ({ Critical: "#991b1b", High: "#ef4444", Medium: "#f59e0b", Low: "#94a3b8", Observation: "#cbd5e1" })[s] || "#cbd5e1";

function SectionHead({ num, title, sub = false, visuals }) {
  if (visuals.minimal) {
    return (
      <div style={{ display: "flex", alignItems: "baseline", gap: "10px", marginBottom: sub ? "10px" : "18px", borderBottom: `1px solid ${visuals.soft}`, paddingBottom: "6px" }}>
        <span style={{ fontSize: "11px", fontFamily: "monospace", color: visuals.muted }}>{num}</span>
        <h2 style={{ fontSize: sub ? "14px" : "17px", fontWeight: 600, margin: 0, color: visuals.text }}>{title}</h2>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: sub ? "10px" : "16px" }}>
      <span style={{ background: sub ? visuals.accent : visuals.primary, color: "white", width: sub ? "24px" : "28px", height: sub ? "24px" : "28px", borderRadius: sub ? "6px" : "8px", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: sub ? "11px" : "14px", fontWeight: 700 }}>{num}</span>
      <h2 style={{ fontSize: sub ? "16px" : "20px", fontWeight: sub ? 600 : 700, margin: 0, color: sub ? visuals.muted : visuals.text }}>{title}</h2>
    </div>
  );
}

function renderContent(content, derived, minimal = false) {
  if (!content) return null;
  if (typeof content === "string") return <p style={{ fontSize: minimal ? "12.5px" : "13px", lineHeight: 1.75, whiteSpace: "pre-wrap", color: "#374151" }}>{content}</p>;
  if (Array.isArray(content)) {
    if (content.length > 0 && typeof content[0] === "object" && (content[0].priority || content[0].action || content[0].timeline)) {
      return (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              {["Priority", "Action", "Owner", "Timeline", "Success Metric"].map((h) => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left", borderBottom: "2px solid #e5e7eb", fontWeight: 700, color: "#6b7280", fontSize: "11px" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {content.map((item, idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "8px 10px", fontWeight: 700, color: "#1f2937", whiteSpace: "nowrap" }}>{item.priority || "N/A"}</td>
                <td style={{ padding: "8px 10px", color: "#374151", lineHeight: 1.5 }}>{item.action || item.recommendation || "N/A"}</td>
                <td style={{ padding: "8px 10px", color: "#374151" }}>{item.owner || "N/A"}</td>
                <td style={{ padding: "8px 10px", color: "#374151", whiteSpace: "nowrap" }}>{item.timeline || "N/A"}</td>
                <td style={{ padding: "8px 10px", color: "#374151", lineHeight: 1.5 }}>{item.success_metric || item.rationale || "N/A"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (content.length > 0 && typeof content[0] === "object" && (content[0].finding_id || content[0].severity || content[0].description))
      return <div>{content.map((item, idx) => renderFindingCard(item, idx, minimal))}</div>;
    return (
      <ul style={{ paddingLeft: "20px", margin: 0 }}>
        {content.map((item, idx) => (
          <li key={idx} style={{ fontSize: "13px", color: "#374151", marginBottom: "4px", lineHeight: 1.6 }}>
            {typeof item === "object" ? item.recommendation || item.action || item.description || item.text || JSON.stringify(item) : String(item)}
          </li>
        ))}
      </ul>
    );
  }
  if (typeof content === "object") {
    if (content.findings && Array.isArray(content.findings))
      return <div>{content.findings.map((i, idx) => renderFindingCard(i, idx, minimal))}</div>;
    return (
      <dl style={{ margin: 0 }}>
        {Object.entries(content).map(([key, val]) => (
          <div key={key} style={{ marginBottom: "8px" }}>
            <dt style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>{key.replace(/_/g, " ")}</dt>
            <dd style={{ fontSize: "13px", color: "#374151", margin: "2px 0 0" }}>{typeof val === "string" ? val : JSON.stringify(val)}</dd>
          </div>
        ))}
      </dl>
    );
  }
  return <p style={{ fontSize: "13px", color: "#374151" }}>{String(content)}</p>;
}

function renderFindingCard(item, idx, minimal = false) {
  if (typeof item === "string") return <p key={idx} className="rd-no-break" style={{ marginBottom: "12px", fontSize: "13px" }}>{item}</p>;
  if (typeof item !== "object") return <p key={idx} className="rd-no-break" style={{ marginBottom: "12px", fontSize: "13px" }}>{String(item)}</p>;

  const controlRef = item.control_ref || item.finding_id || item.control_id || item.id || `Finding ${idx + 1}`;
  const rawDescription = item.description || item.finding_description || item.finding_text || item.title || "";
  const description = stripTech(rawDescription);
  const evidenceNarrative = item.evidence_narrative || item.summary || item.details || "";
  const verdict = item.verdict || item.status || "INCONCLUSIVE";
  const rawWhere = item.where_to_look;
  const whereToLook = Array.isArray(rawWhere) ? rawWhere : (typeof rawWhere === "string" ? [rawWhere] : []);
  const severity = (item.severity) || (verdict === "FAIL" ? "High" : verdict === "INCONCLUSIVE" ? "Observation" : "Low");

  return (
    <div
      key={idx}
      className="rd-no-break rd-finding-card"
      style={{
        marginBottom: minimal ? "14px" : "16px",
        padding: minimal ? "14px 16px" : "20px",
        border: `1px solid ${minimal ? "#eceff3" : "#e5e7eb"}`,
        borderLeft: `3px solid ${minimal ? sevLineColor(severity) : sevBorderColor(severity)}`,
        borderRadius: minimal ? "2px" : "8px",
        backgroundColor: minimal ? "#ffffff" : sevBgColor(severity),
        pageBreakInside: "avoid",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "12px" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <span style={{ fontWeight: 800, fontSize: "12px", color: sevBorderColor(severity), fontFamily: "monospace", letterSpacing: "0.5px" }}>{controlRef}</span>
            <span style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "9999px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", backgroundColor: verdict === "FAIL" ? "#fef2f2" : "#f8fafc", color: verdict === "FAIL" ? "#ef4444" : "#64748b", border: `1px solid ${verdict === "FAIL" ? "#fecaca" : "#cbd5e1"}` }}>
              {verdict}
            </span>
          </div>
          <p style={{ fontSize: "14px", fontWeight: 700, color: "#1f2937", margin: "0 0 8px 0", lineHeight: 1.4 }}>{description}</p>
        </div>
      </div>

      {evidenceNarrative && (
        <div style={{ marginLeft: "28px", marginBottom: "12px", padding: "12px", backgroundColor: "rgba(0,0,0,0.02)", borderRadius: "6px", border: "1px solid rgba(0,0,0,0.05)" }}>
          <p style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#9ca3af", marginBottom: "4px" }}>Evidence Narrative</p>
          <p style={{ fontSize: "12px", color: "#1f2937", lineHeight: 1.6 }}>{evidenceNarrative}</p>
        </div>
      )}

      {whereToLook.length > 0 && (
        <div style={{ marginLeft: "28px", display: "flex", flexDirection: "column", gap: "4px" }}>
          <p style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#9ca3af", marginBottom: "2px" }}>Where to Look</p>
          <ul style={{ paddingLeft: "16px", margin: 0, fontSize: "11px", color: "#4b5563" }}>
            {whereToLook.map((w, wIdx) => <li key={wIdx} style={{ marginBottom: "2px", lineHeight: 1.4 }}>{String(w)}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   ReportDocument — the shared printable document
   ══════════════════════════════════════════════════════════ */
function ReportDocument({
  report,
  context = {},
  derived = {},
  settings = {},
  signoff = {},
  printMode = false,
}) {
  const { report: _report } = { report };
  const visuals = THEMES[settings.theme] || THEMES.modern;
  const minimal = !!visuals.minimal;

  const sections = report?.sections || {};
  const stats = report?.stats || {};
  const {
    orgName = "Organization",
    auditType = "Compliance",
    auditName = "",
    refId = "AUD-0000-0000",
    reportDate = new Date(),
    complianceScore = 0,
    riskScore = 0,
    riskRating = "Low",
    totalControls = 0,
    totalFindings = 0,
    findingsLabel = "Total Findings",
    severityCounts = {},
    sevData = [],
    processData = [],
    maturityData = [],
    heatmapData = [],
    maxHeat = 1,
    trendData = [],
    glossary = [],
    distributionRoles = [],
    tocItems = [],
  } = derived;

  const isRelianceRetailDemo = derived.isRelianceRetailDemo || false;
  const findingsDetail = derived.sortedFindings?.length ? derived.sortedFindings : sections.findings_detail;

  const chartAnimProps = (base, begin) => (printMode ? { isAnimationActive: false } : { isAnimationActive: true, animationDuration: 1200, animationBegin: begin });

  const knownKeys = new Set(["executive_summary", "scope", "methodology", "findings_detail", "severity_classification", "risk_exposure", "recommendations", "conclusion"]);

  return (
    <div
      className={`report-document rd-theme-${settings.theme || "modern"}`}
      style={{
        position: "relative",
        fontFamily: visuals.fontFamily,
        color: visuals.text,
        lineHeight: 1.7,
        backgroundColor: "#ffffff",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {settings.showWatermark !== false && (
        <div style={{ position: "absolute", top: "40%", left: "50%", transform: "translate(-50%,-50%) rotate(-35deg)", fontSize: minimal ? "72px" : "100px", fontWeight: minimal ? 500 : 900, color: minimal ? "rgba(0,0,0,0.022)" : "rgba(0,0,0,0.03)", pointerEvents: "none", zIndex: 0, whiteSpace: "nowrap", userSelect: "none", letterSpacing: minimal ? "14px" : "8px" }}>
          CONFIDENTIAL
        </div>
      )}

      <div style={{ position: "relative", zIndex: 1, padding: "30px" }}>
        {/* cover */}
        {settings.includeCover !== false && (
          <div style={{ pageBreakAfter: "always", textAlign: "center", padding: minimal ? "56px 24px" : "24px", background: minimal ? "#ffffff" : visuals.coverBg, border: minimal ? "none" : `2px solid ${visuals.coverBorder}`, borderRadius: minimal ? 0 : "14px", position: "relative", overflow: "hidden" }}>
            {!minimal && <div style={{ position: "absolute", inset: "14px", border: `1px solid ${visuals.coverFrame}`, borderRadius: "10px", pointerEvents: "none" }} />}
            <div style={{ position: "relative", zIndex: 1, background: minimal ? "#ffffff" : "rgba(255,255,255,0.78)", borderRadius: minimal ? 0 : "10px", padding: minimal ? "64px 20px 48px" : "74px 28px 56px" }}>
              {settings.showConfidential !== false && (
                minimal ? (
                  <p style={{ fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", color: visuals.muted, margin: "0 0 48px", fontWeight: 600 }}>
                    Confidential — Restricted Distribution
                  </p>
                ) : (
                  <div style={{ background: "#b91c1c", color: "white", padding: "6px 20px", display: "inline-block", borderRadius: "4px", fontSize: "11px", fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", marginBottom: "40px" }}>
                    CONFIDENTIAL - RESTRICTED DISTRIBUTION
                  </div>
                )
              )}
              <h1 style={{ fontSize: minimal ? "34px" : "36px", fontWeight: minimal ? 300 : 800, color: visuals.text, margin: "0 0 10px", letterSpacing: "-0.5px" }}>{auditType}</h1>
              <h2 style={{ fontSize: minimal ? "17px" : "22px", fontWeight: 400, color: visuals.muted, margin: "0 0 44px" }}>Audit Report</h2>
              <div style={{ width: "60px", height: minimal ? "1px" : "3px", background: minimal ? "#d1d5db" : visuals.primary, margin: "0 auto 40px", borderRadius: "2px" }} />
              <table style={{ margin: "0 auto", borderCollapse: "collapse", fontSize: "13px", color: visuals.muted }}>
                <tbody>
                  {[
                    ["Audit Name", auditName || "—"],
                    ["Organization", orgName],
                    ["Audit Type", auditType],
                    ["Report Date", reportDate.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })],
                    ["Classification", report?.classification || "Confidential"],
                    ["Report Version", report?.report_version ?? 1],
                  ].map(([label, value], i) => (
                    <tr key={i}>
                      <td style={{ padding: "6px 20px 6px 0", fontWeight: 600, color: visuals.muted, textAlign: "right", borderBottom: `1px solid ${visuals.soft}` }}>{label}</td>
                      <td style={{ padding: "6px 0 6px 20px", borderBottom: "1px solid #f3f4f6" }}>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: "60px", fontSize: "10px", color: "#9ca3af" }}>
                <p>This document is confidential and intended solely for the named recipient(s).</p>
                <p>Unauthorised disclosure, copying, or distribution is strictly prohibited.</p>
              </div>
            </div>
          </div>
        )}

        {/* TOC */}
        {settings.includeToc !== false && tocItems.length > 0 && (
          <div style={{ pageBreakAfter: "always", paddingTop: "30px" }}>
            <h2 style={{ fontSize: minimal ? "17px" : "20px", fontWeight: minimal ? 600 : 700, borderBottom: minimal ? `1px solid ${visuals.soft}` : `2px solid ${visuals.primary}`, paddingBottom: "8px", marginBottom: "24px", color: visuals.text }}>
              Table of Contents
            </h2>
            <div style={{ fontSize: "13px" }}>
              {tocItems.map((item, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "baseline", padding: "6px 0", borderBottom: `1px dotted ${visuals.soft}` }}>
                  <span style={{ fontWeight: 600, color: visuals.primary, minWidth: "36px" }}>{item.num}</span>
                  <span style={{ flex: 1 }}>{item.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* sections */}
        {(() => {
          let n = 1;
          return (
            <>
              {sections.executive_summary && (
                <div style={{ marginBottom: "30px" }}>
                  <SectionHead visuals={visuals} num={String(n++)} title="Executive Summary" />
                  <div style={{ background: minimal ? "transparent" : visuals.surface, borderLeft: minimal ? "none" : `4px solid ${visuals.primary}`, padding: minimal ? "0 0 4px" : "16px 20px", borderRadius: minimal ? 0 : "0 8px 8px 0", fontSize: "13px", lineHeight: 1.8, color: visuals.muted }}>
                    {renderContent(sections.executive_summary, derived, minimal)}
                  </div>
                  <div style={{ display: "flex", gap: "16px", marginTop: "20px", flexWrap: "wrap" }}>
                    {[
                      { label: "Total Controls", value: totalControls, color: "#dc2626" },
                      { label: findingsLabel, value: totalFindings, color: "#dc2626" },
                      { label: "Compliance Score", value: `${complianceScore}%`, color: complianceScore >= 70 ? "#16a34a" : "#d97706" },
                      { label: "Risk Rating", value: riskRating, color: (String(riskRating).toLowerCase() === "critical" || String(riskRating).toLowerCase() === "high") ? "#dc2626" : String(riskRating).toLowerCase() === "medium" ? "#d97706" : "#16a34a" },
                    ].map((s, i) => (
                      <div key={i} style={{ flex: "1", minWidth: "140px", background: "transparent", border: minimal ? "none" : `1px solid ${visuals.soft}`, borderTop: minimal ? `1px solid ${visuals.soft}` : "none", borderRadius: minimal ? 0 : "8px", padding: minimal ? "14px 10px 4px" : "12px 16px", textAlign: "center" }}>
                        {!minimal && <div style={{ width: "26px", height: "4px", borderRadius: "4px", background: s.color, margin: "0 auto 8px" }} />}
                        <div style={{ fontSize: minimal ? "24px" : "22px", fontWeight: minimal ? 600 : 800, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
                        <div style={{ fontSize: "10px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {sections.scope && (
                <div style={{ marginBottom: "30px", pageBreakInside: "avoid" }}>
                  <SectionHead visuals={visuals} num={String(n++)} title="Audit Scope & Objectives" />
                  <div style={{ fontSize: "13px", color: "#374151" }}>{renderContent(sections.scope, derived, minimal)}</div>
                </div>
              )}

              {sections.methodology && (
                <div style={{ marginBottom: "30px", pageBreakInside: "avoid" }}>
                  <SectionHead visuals={visuals} num={String(n++)} title="Methodology" />
                  <div style={{ fontSize: "13px", color: "#374151" }}>{renderContent(sections.methodology, derived, minimal)}</div>
                </div>
              )}

              {settings.includeCharts !== false && (
                <div style={{ pageBreakBefore: "always", marginBottom: "30px" }}>
                  <SectionHead visuals={visuals} num={String(n++)} title="Executive Dashboard" />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "14px" }}>
                    <div style={{ border: minimal ? "1px solid #eceff3" : "1px solid #e5e7eb", borderRadius: minimal ? "2px" : "8px", padding: minimal ? "8px" : "10px", background: "#fff" }}>
                      <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px", fontWeight: 700 }}>Compliance Score</div>
                      <RiskGauge score={complianceScore || 78} label="Overall Compliance" size={140} staticFrame={printMode} />
                    </div>
                    <div style={{ border: minimal ? "1px solid #eceff3" : "1px solid #e5e7eb", borderRadius: minimal ? "2px" : "8px", padding: minimal ? "8px" : "10px", background: "#fff" }}>
                      <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px", fontWeight: 700 }}>Maturity Radar</div>
                      <div style={{ width: "100%", height: "160px" }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart data={maturityData}>
                            <PolarGrid stroke="#e5e7eb" />
                            <PolarAngleAxis dataKey="domain" tick={{ fontSize: 8 }} />
                            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 8 }} />
                            <Radar name="Score" dataKey="score" stroke="#dc2626" fill="#dc2626" fillOpacity={0.3} {...chartAnimProps()} />
                            <Radar name="Benchmark" dataKey="benchmark" stroke="#94a3b8" fill="none" strokeDasharray="4 4" />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div style={{ border: minimal ? "1px solid #eceff3" : "1px solid #e5e7eb", borderRadius: minimal ? "2px" : "8px", padding: minimal ? "8px" : "10px", background: "#fff" }}>
                      <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px", fontWeight: 700 }}>Trend</div>
                      <div style={{ width: "100%", height: "160px" }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={trendData}>
                            <defs>
                              <linearGradient id="rdCg" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} /><stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                              </linearGradient>
                              <linearGradient id="rdFg" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3} /><stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="period" tick={{ fontSize: 8 }} />
                            <YAxis tick={{ fontSize: 8 }} />
                            <Area type="monotone" dataKey="compliance" stroke="#22c55e" fill="url(#rdCg)" name="Compliance %" {...chartAnimProps()} />
                            <Area type="monotone" dataKey="findings" stroke="#ef4444" fill="url(#rdFg)" name="Findings" {...chartAnimProps()} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
                    <div style={{ border: minimal ? "1px solid #eceff3" : "1px solid #e5e7eb", borderRadius: minimal ? "2px" : "8px", padding: minimal ? "8px" : "10px", background: "#fff" }}>
                      <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px", fontWeight: 700 }}>Severity Distribution</div>
                      <div style={{ width: "100%", height: "180px" }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={sevData} cx="50%" cy="50%" innerRadius={35} outerRadius={65} paddingAngle={3} dataKey="value" {...chartAnimProps()}>
                              {sevData.map((e, i) => <Cell key={i} fill={e.fill || sevColor(e.name)} />)}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div style={{ border: minimal ? "1px solid #eceff3" : "1px solid #e5e7eb", borderRadius: minimal ? "2px" : "8px", padding: minimal ? "8px" : "10px", background: "#fff" }}>
                      <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px", fontWeight: 700 }}>By Process Area</div>
                      <div style={{ width: "100%", height: "180px" }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={processData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis type="number" tick={{ fontSize: 8 }} />
                            <YAxis dataKey="name" type="category" tick={{ fontSize: 8 }} width={90} />
                            <Bar dataKey="value" fill="#dc2626" radius={[0, 4, 4, 0]} {...chartAnimProps()} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div style={{ border: minimal ? "1px solid #eceff3" : "1px solid #e5e7eb", borderRadius: minimal ? "2px" : "8px", padding: minimal ? "8px" : "10px", background: "#fff" }}>
                      <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "6px", fontWeight: 700 }}>Risk Heatmap</div>
                      <div style={{ fontSize: "10px" }}>
                        {["Very High", "High", "Medium", "Low", "Very Low"].map((lk, li) => (
                          <div key={li} style={{ display: "flex", alignItems: "center", gap: "2px", marginBottom: "2px" }}>
                            <span style={{ width: "54px", textAlign: "right", color: "#6b7280", fontSize: "8px" }}>{lk}</span>
                            {[0, 1, 2, 3, 4].map((ii) => (
                              <div key={ii} style={{ flex: 1 }}>
                                <HeatmapCell value={heatmapData[4 - li]?.[ii] || 0} max={maxHeat} label={`${lk} x Impact ${ii + 1}`} />
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {sections.findings_detail && (() => {
                const fNum = n++;
                return (
                  <>
                    <div style={{ pageBreakBefore: "always", marginBottom: "30px" }}>
                      <SectionHead visuals={visuals} num={String(fNum)} title="Detailed Findings" />
                      <div style={{ fontSize: "13px", color: "#374151" }}>
                        {findingsDetail?.length ? renderContent(findingsDetail, derived, minimal) : null}
                      </div>
                    </div>
                    {sections.severity_classification && (
                      <div style={{ marginBottom: "30px", pageBreakInside: "avoid" }}>
                        <SectionHead visuals={visuals} num={`${fNum}.1`} title="Severity Classification" sub />
                        <div style={{ fontSize: "13px", color: "#374151" }}>{renderContent(sections.severity_classification, derived, minimal)}</div>
                      </div>
                    )}
                    {sections.risk_exposure && (
                      <div style={{ marginBottom: "30px", pageBreakInside: "avoid" }}>
                        <SectionHead visuals={visuals} num={`${fNum}.2`} title="Risk Exposure Overview" sub />
                        <div style={{ fontSize: "13px", color: "#374151" }}>{renderContent(sections.risk_exposure, derived, minimal)}</div>
                      </div>
                    )}
                  </>
                );
              })()}

              {sections.recommendations && (
                <div style={{ pageBreakBefore: "always", marginBottom: "30px" }}>
                  <SectionHead visuals={visuals} num={String(n++)} title="Recommendations & Action Plan" />
                  <div style={{ fontSize: "13px", color: "#374151" }}>{renderContent(sections.recommendations, derived, minimal)}</div>
                </div>
              )}

              {sections.conclusion && (
                <div style={{ marginBottom: "30px", pageBreakInside: "avoid" }}>
                  <SectionHead visuals={visuals} num={String(n++)} title="Conclusion" />
                  <div style={{ background: minimal ? "transparent" : "#f0fdf4", borderLeft: minimal ? "3px solid #16a34a" : "4px solid #22c55e", padding: minimal ? "0 0 4px 14px" : "16px 20px", borderRadius: minimal ? 0 : "0 8px 8px 0", fontSize: "13px", lineHeight: 1.8, color: "#374151" }}>
                    {renderContent(sections.conclusion, derived, minimal)}
                  </div>
                </div>
              )}

              {Object.entries(sections).filter(([k]) => !knownKeys.has(k)).map(([key, content]) => (
                <div key={key} style={{ marginBottom: "30px", pageBreakInside: "avoid" }}>
                  <SectionHead visuals={visuals} num={String(n++)} title={key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} />
                  <div style={{ fontSize: "13px", color: "#374151" }}>{renderContent(content, derived, minimal)}</div>
                </div>
              ))}
            </>
          );
        })()}

        {/* appendices */}
        {settings.includeAppendix !== false && (
          <div style={{ pageBreakBefore: "always" }}>
            <div className="rd-no-break" style={{ marginBottom: "30px" }}>
              <h2 style={{ fontSize: minimal ? "17px" : "20px", fontWeight: minimal ? 600 : 700, borderBottom: minimal ? `1px solid ${visuals.soft}` : `2px solid ${visuals.primary}`, paddingBottom: "8px", marginBottom: "24px", color: visuals.text }}>Appendices</h2>
            </div>

            <div className="rd-no-break" style={{ marginBottom: "30px" }}>
              <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#374151", marginBottom: "12px" }}>Appendix A — Statistics Summary</h3>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <tbody>
                  {[
                    ["Total Controls Assessed", totalControls],
                    ["Total Findings Identified", totalFindings],
                    ["Compliance Score", `${complianceScore}%`],
                    ["Residual Risk Score", `${riskScore}%`],
                    ["Risk Rating", riskRating],
                    ...(stats.team_findings ? [["Team Validated Findings", stats.team_findings]] : []),
                    ...Object.entries(severityCounts).map(([k, v]) => [`${k} Severity Findings`, v]),
                    ["Report Generated", reportDate.toLocaleString()],
                  ].map(([label, value], i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "6px 12px", fontWeight: 500, color: "#6b7280", width: "50%" }}>{label}</td>
                      <td style={{ padding: "6px 12px", fontWeight: 600, fontFamily: "monospace" }}>{String(value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rd-no-break" style={{ marginBottom: "30px" }}>
              <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#374151", marginBottom: "12px" }}>Appendix B — Severity Definitions</h3>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead><tr style={{ background: "#f9fafb" }}>{["Level", "Definition", "Expected Response"].map((h) => (<th key={h} style={{ padding: "8px 12px", textAlign: "left", borderBottom: "2px solid #e5e7eb", fontWeight: 700, fontSize: "11px", color: "#6b7280" }}>{h}</th>))}</tr></thead>
                <tbody>
                  {[
                    { level: "Critical", color: "#991b1b", def: "Fundamental control failure presenting immediate risk of regulatory action, significant financial loss, or material compliance breach.", response: "Immediate remediation within 30 days. Board notification mandatory." },
                    { level: "High", color: "#ef4444", def: "Significant control weakness that could lead to regulatory findings, financial exposure, or compliance gaps.", response: "Remediation within 60 days. Senior management oversight required." },
                    { level: "Medium", color: "#f59e0b", def: "Moderate control deficiency that presents a potential risk if not addressed in a timely manner.", response: "Remediation within 90 days. Management action plan required." },
                    { level: "Low", color: "#64748b", def: "Minor control enhancement opportunity that represents best practice improvement.", response: "Address within next audit cycle. Monitor for deterioration." },
                    { level: "Observation", color: "#94a3b8", def: "Informational observation or enhancement suggestion with no immediate risk.", response: "Consider for future improvement. No formal tracking required." },
                  ].map((row, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontWeight: 700, color: row.color }}>
                          <span style={{ width: "8px", height: "8px", borderRadius: "2px", background: row.color, display: "inline-block" }} />
                          {row.level}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", lineHeight: 1.5 }}>{row.def}</td>
                      <td style={{ padding: "8px 12px", lineHeight: 1.5, fontSize: "11px", color: "#6b7280" }}>{row.response}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {glossary.length > 0 && (
              <div className="rd-no-break" style={{ marginBottom: "30px" }}>
                <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#374151", marginBottom: "12px" }}>Appendix C — Glossary</h3>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <tbody>
                    {glossary.map((g, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "6px 12px", fontWeight: 700, color: "#dc2626", whiteSpace: "nowrap", verticalAlign: "top", width: "120px" }}>{g.term}</td>
                        <td style={{ padding: "6px 12px", lineHeight: 1.5 }}>{g.definition}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <ReportSignoff
              distributionRoles={distributionRoles}
              signoff={signoff}
              letter={glossary.length > 0 ? "D" : "C"}
            />

            <ReportIntegrity report={report} />
          </div>
        )}

        {/* footer */}
        <div style={{ marginTop: "40px", paddingTop: "16px", borderTop: minimal ? `1px solid ${visuals.soft}` : `2px solid ${visuals.soft}`, display: "flex", justifyContent: "space-between", fontSize: "9px", color: "#9ca3af" }}>
          <div>
            <p style={{ margin: 0 }}>{auditType} Audit Report — {orgName}</p>
            <p style={{ margin: "2px 0 0" }}>Reference: {refId}</p>
          </div>
          <div style={{ textAlign: "right" }}>
            <p style={{ margin: 0 }}>Generated: {reportDate.toLocaleString()}</p>
            <p style={{ margin: "2px 0 0" }}>Classification: {report?.classification || "Confidential"}</p>
          </div>
        </div>

        <div style={{ marginTop: "20px", padding: minimal ? "12px 0 0" : "12px 16px", background: minimal ? "transparent" : "#fefce8", border: minimal ? `1px solid ${visuals.soft}` : "1px solid #fde68a", borderRadius: minimal ? 0 : "8px", fontSize: "10px", color: minimal ? "#6b7280" : "#92400e", lineHeight: 1.6 }}>
          <strong>Disclaimer:</strong> This report has been prepared for the exclusive use of {orgName} management and its governing body. The findings and recommendations contained herein are based on information available at the time of the audit and represent the professional judgement of the audit team. This report should not be shared with external parties without prior written consent.
        </div>
      </div>
    </div>
  );
}

/* Memoized so unrelated state churn in the page (e.g. typing in the sign-off
   card) does not re-render this large document subtree on every keystroke. */
export default memo(ReportDocument);